import { ref, onMounted, watch } from 'vue'

import {
  UserAgentOptions,
  RegistererOptions,
  UserAgent,
  Web,
  Registerer, 
  Invitation,
  SessionState,
  Inviter  
} from "sip.js";

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import utc from 'dayjs/plugin/utc'

dayjs.extend(duration)
dayjs.extend(utc)

const localStream = ref(null);
const remoteStream = ref(null);

const localStreamGain = ref(null);
const remoteStreamGain = ref(null);
const localVolume = ref(1.0);
const remoteVolume = ref(1.0);

const inputDevice = ref(null);
const outputDevice = ref(null);

const audioConstraints = ref({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
});

const CALL_IDLE = 0;
const CALL_RINGING = 1;
const CALL_ESTABLISHED = 2;
const CALL_FAILED = 3;

const sipStatus = ref({connection: false,
                       registration: false,
                       callstatus: CALL_IDLE,
                       timestamp: null
});

const dataPoints = 20;

const defaultPerfHistory = () => {
   return Array.from({ length: dataPoints }, () => ({
                decode: { loss: 0, jitter: 0, rtt: 0 },
                encode: { loss: 0, jitter: 0, rtt: 0 }
              }))
            }

const performanceHistory = ref(defaultPerfHistory());

const getDefaultCallStats = () => ({
  encode: {
    codec: '',
    bitrate: 0,
    sampleRate: 0,
    loss: 0,
    jitter: 0,
    rtt: 0
  },
  decode: {
    codec: '',
    bitrate: 0,
    sampleRate: 0,
    loss: 0,
    jitter: 0,
    rtt: 0
  },
});

const callStats = ref(getDefaultCallStats());

let statsInterval = null;

let inputAudioContext: AudioContext | null;
let inputRawStream: MediaStream | null;

//let userAgent: UserAgent | undefined;
//let session: Inviter | undefined;

const userAgent = ref(UserAgent)
const session = ref(Inviter)

const activeCall = ref(null);

export function connectLogic() {
  onMounted(() => {
    console.log('Page logic initialized!')
  })

  // Return everything the HTML template needs
  return {
    localStream,
    remoteStream,
    userAgent,
    session,
  }
}


export function webRTCService() {
  return { sipStatus, CALL_IDLE, CALL_RINGING, CALL_ESTABLISHED, CALL_FAILED, callStats, activeCall, getDuration, inputDevice, outputDevice, updateGain };
}

export function audioConst() {
  return { audioConstraints, updateAudioConstraints };
}

export function perfStats() {
  return { performanceHistory, dataPoints }
}

const updateGain = (direction: string, level: number, mute: boolean) => {
  let volume = level
  if (mute) {
    volume = 0
  }

  if (direction === "local") {
    localVolume.value = volume
    if (localStreamGain.value !== null) {
      localStreamGain.value.gain.value = volume
    }
  } else if (direction === "remote") {
    remoteVolume.value = volume
    if (remoteStreamGain.value !== null) {
      remoteStreamGain.value.gain.value = volume
    }
  }
}

const getDuration = () => {
  if (sipStatus.value.timestamp === null) {
    return null
  }
  
  const now = dayjs()
  const diffInMs = now.diff(sipStatus.value.timestamp)
  return dayjs.utc(diffInMs).format('HH:mm:ss')
}

const forceHighQualityOpus = (description: { sdp: string }) => {
  // Find the Opus payload type (usually 111 in WebRTC)
  // Look for a=rtpmap:XX opus/48000/2
  const sdp = description.sdp;
  
  // Update the fmtp line for Opus
  // stereo=1: receiver prefers stereo
  // sprop-stereo=1: sender is producing stereo
  // maxaveragebitrate=64000: set to 64kbps (bits per second)
  const modifiedSdp = sdp.replace(
    /a=fmtp:(\d+) (.*)/g,
    (line, payloadType, existingParams) => {
      if (sdp.includes(`a=rtpmap:${payloadType} opus/48000/2`)) {
        return `a=fmtp:${payloadType} ${existingParams};stereo=0;sprop-stereo=0;maxaveragebitrate=64000`;
      }
      return line;
    }
  );

  description.sdp = modifiedSdp;
  return Promise.resolve(description);
};

const startStatsPolling = (pc) => {
  // Clear any existing poller
  if (statsInterval) clearInterval(statsInterval);

  statsInterval = setInterval(async () => {
    const stats = await pc.getStats();
    
    stats.forEach((report: any) => {
      // 1. Get Codec & Sample Rate
      let updatePerf = false
      switch(report.type) {
        case 'inbound-rtp':
          if (report.kind === 'audio') {
            const totalPackets = (report.packetsReceived | 0) + (report.packetsLost | 0);
            callStats.value.decode.loss = totalPackets > 0 ? Math.round(((report.packetsLost | 0)) / totalPackets * 100) : 0;
            callStats.value.decode.jitter = report.jitter ? Math.round(report.jitter * 1000) : 0; // Convert to ms
            updatePerf = true
          }
          break;
        case 'remote-inbound-rtp': // Stats about what they are receiving from us
          if (report.kind === 'audio') {
            const totalPackets = (report.packetsReceived | 0) + (report.packetsLost | 0);
            callStats.value.encode.loss = totalPackets > 0 ? Math.round(((report.packetsLost | 0)) / totalPackets * 100) : 0;
            callStats.value.encode.jitter = report.jitter ? Math.round(report.jitter * 1000) : 0; // Convert to ms
            callStats.value.encode.rtt = report.roundTripTime ? Math.round(report.roundTripTime * 1000) : 0; // Convert to ms            
            updatePerf = true
          }
          break;
        case 'codec':
          if (report.mimeType && report.mimeType.includes('audio')) {
            let codecType = report.codecType === 'encode' ? 'encode' : 'decode';
            callStats.value[codecType].codec = report.mimeType.replace('audio/', '');
            callStats.value[codecType].sampleRate = report.clockRate;
    
            const sdpFmtpLine: Record<string, string> = Object.fromEntries(
              (report.sdpFmtpLine || '').split(';').map((pair:string) => pair.split('='))
            );
    
            callStats.value[codecType].bitrate = sdpFmtpLine.maxaveragebitrate ? parseInt(sdpFmtpLine.maxaveragebitrate) / 1000 : 0; 
          }
          break;
        default:
          //console.log(`Other Report (${report.type}):`, report);
          break;
      }

      if (updatePerf) {
        let stats = callStats.value
        performanceHistory.value.shift();
        performanceHistory.value.push({
          decode: { loss: stats.decode.loss, jitter: stats.decode.jitter},
          encode: { loss: stats.encode.loss, jitter: stats.encode.jitter, rtt: stats.encode.rtt },
        });            
      }

      //console.log(callStats.value.encode, callStats.value.decode);
    });
  }, 5000); // Run every 5 seconds
};

const stopStatsPolling = () => {
  if (statsInterval) {
    clearInterval(statsInterval);
    statsInterval = null;
    callStats.value = getDefaultCallStats(); // Reset stats when call ends
  }
};

async function getProcessedStream(constraints: MediaStreamConstraints): Promise<MediaStream> {
  try {

    if (inputDevice) {
      if (typeof constraints.audio !== 'object') {
        constraints.audio = audioConstraints.value;
      }

      constraints.audio.deviceId = { exact: inputDevice.value };
    }

    console.log("Acquiring media with constraints:", constraints);


    inputAudioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 48000 });
    inputRawStream = await navigator.mediaDevices.getUserMedia(constraints);

    // 2. CHROME SPECIFIC: Resume context if it was created in a suspended state
    // This must technically be called within a user gesture (like a click)
    if (inputAudioContext.state === 'suspended') {
      await inputAudioContext.resume();
      console.log("AudioContext resumed successfully.");
    }
    
    localStreamGain.value = inputAudioContext.createGain();
    localStreamGain.value.gain.value = localVolume.value;
  
    const source = inputAudioContext.createMediaStreamSource(inputRawStream);
    const destination = inputAudioContext.createMediaStreamDestination();
    destination.channelCount = 2; // Ensure mono output
    localStream.value = destination.stream;
    
    //source.connect(destination);
    source.connect(localStreamGain.value)
    localStreamGain.value.connect(destination)
    
    // Here you can add any processing to the stream if needed
    // For example, you could use the Web Audio API to modify the audio
    
    return destination.stream;
  } catch (err) {
    console.error("Error acquiring media stream:", err);
    throw err;
  }
}

export async function initWebRTC(account, secret) {
   //account = '2102';
  // secret = 'StrongPassword2102';
  //const server = "wss://192.168.20.25:8089/ws";
  const server = "wss://remotestudio.2me.com.au/webrtc-ws";
  const domain = "remotestudio.2me.com.au";
  let registerer = Registerer;


  // 1. Custom factory for manual getUserMedia control
  const localMediaStreamFactory = async (constraints: any) => {
    console.log("Acquiring media with constraints:", constraints);
    return await getProcessedStream(constraints);
  };
  
  // 2. Wrap it in an SDH factory
  const localSDHFactory = Web.defaultSessionDescriptionHandlerFactory(localMediaStreamFactory);
  
  const userAgentOptions: UserAgentOptions = {
    uri: UserAgent.makeURI(`sip:${account}@${domain}`),
    logLevel: "debug",
    logBuiltinEnabled: true,
    transportOptions: {
      server: server,
      keepAliveInterval: 20, // Send CRLF keep-alives every 20 seconds
      keepAliveDebounce: 10,
      traceSip: true 
    },
    authorizationUsername: account,
    authorizationPassword: secret,
    displayName: account,
    sessionDescriptionHandlerFactory: localSDHFactory,
    // Delegate handles transport-level events
    delegate: {
      onConnect: () => { sipStatus.value.connection = true; },
      onDisconnect: (error: any) => {
        sipStatus.value.connection = false

        // Wait 5 seconds, then attempt to reconnect the UserAgent
        setTimeout(async () => {
          try {
            await userAgent.value.reconnect();
            // If you use a Registerer, you may need to call register() here:
            await registerer.register();
          } catch (e) {
            console.error("Reconnection attempt failed:", e);
          }
        }, 5000);        
      },
      onInvite: (invitation: Invitation) => {
        // Handle incoming calls here
        console.log("Incoming call from:", invitation.remoteIdentity.uri.toString());
      }
    }
  };

  userAgent.value = new UserAgent(userAgentOptions);

  // Setup Registerer
  const registererOptions: RegistererOptions = {};
  registerer = new Registerer(userAgent.value, registererOptions);

  // Monitor registration state
  registerer.stateChange.addListener((state: string) => {
    sipStatus.value.registration = state === "Registered";
  });

  // Start the UserAgent and Register
  await userAgent.value.start();
  await registerer.register();

  return { userAgent, registerer };
}

function stopLocalMedia() {
  // Stop the actual hardware microphone
  if (inputRawStream) {
    inputRawStream.getTracks().forEach(track => {
      track.stop();
      console.log("Hardware mic stopped");
    });
    //inputRawStream. = null;
  } else {
    console.log("No local media stream to stop");
  }

  // Close the Web Audio context
  if (inputAudioContext && inputAudioContext.state !== 'closed') {
    inputAudioContext.close();
    inputAudioContext = null;
    console.log("AudioContext closed");
  }
}

async function updateAudioConstraints() {
  if (inputRawStream) {
    inputRawStream.getTracks().forEach(async track => {
      await track.applyConstraints(audioConstraints.value);
      //console.log("Applied new audio constraints to track:", track, audioConstraints.value);
    });
  }
  //} else {
    //console.log("No local media stream to update");
  //}
}

export async function makeCall(userAgent: UserAgent, targetNumber: string, callback: Function) {
  const target = UserAgent.makeURI(`sip:${targetNumber}@192.168.20.25`);
  
  if (!target) {
    throw new Error("Failed to create target URI");
  }

  // 1. Create the Inviter (the outgoing Session)
  session.value = new Inviter(userAgent, target, {
    sessionDescriptionHandlerModifiers: [forceHighQualityOpus],
    sessionDescriptionHandlerOptions: {
      constraints: { audio: true, video: false }
    }
  });

  activeCall.value = targetNumber

  // 2. Monitor session state changes
  session.value.stateChange.addListener((newState: SessionState) => {
    switch (newState) {
      case SessionState.Establishing:
        sipStatus.value.callstatus = CALL_RINGING;
        break;
      case SessionState.Established:
        performanceHistory.value = defaultPerfHistory()
        sipStatus.value.callstatus = CALL_ESTABLISHED;
        sipStatus.value.timestamp = dayjs()
        setupRemoteMedia(session.value);
        const pc = session.value.sessionDescriptionHandler.peerConnection;
        startStatsPolling(pc);
        break;
      case SessionState.Terminated:
        console.log("Call ended.");

        if (sipStatus.value.callstatus != CALL_ESTABLISHED) {
          sipStatus.value.callstatus = CALL_FAILED
        } else {
          sipStatus.value.callstatus = CALL_IDLE  
        }

        activeCall.value = null
        sipStatus.value.timestamp = null

        stopLocalMedia();
        stopStatsPolling();
        break;
    }
  });

  // 3. Send the INVITE request
  await session.value.invite();
  
  return session;
}

/**
 * Helper to attach the remote audio stream to your <audio> element
 */
function setupRemoteMediaOld(session: Inviter) {
  const remoteAudio = document.getElementById("remoteAudio") as HTMLAudioElement;
  remoteStream.value = new MediaStream();

  const pc = session.sessionDescriptionHandler.peerConnection;

  pc.getReceivers().forEach((receiver: any) => {
    if (receiver.track) {
      remoteStream.value.addTrack(receiver.track);
    }
  });

  // --- GAIN CONTROL LOGIC ---
  const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const source = audioCtx.createMediaStreamSource(remoteStream.value);
  remoteStreamGain.value = audioCtx.createGain();

  // Set initial volume (1.0 is normal, >1.0 is boosted)
  remoteStreamGain.value.gain.value = remoteVolume.value;

  // Create a destination for the processed audio
  const destination = audioCtx.createMediaStreamDestination();

  // Connect: Source -> Gain -> Destination
  source.connect(remoteStreamGain.value);
  remoteStreamGain.value.connect(destination);

  // Use the output stream from the GainNode instead of the raw stream
  remoteAudio.srcObject = destination.stream;
  // --------------------------

  if (outputDevice) {
    (remoteAudio as any).setSinkId(outputDevice.value);
  }
  
  remoteAudio.play();
}

function setupRemoteMedia(session: Inviter) {
  const remoteAudio = document.getElementById("remoteAudio") as HTMLAudioElement;
  remoteStream.value = new MediaStream();

  const pc = session.sessionDescriptionHandler.peerConnection;

  pc.getReceivers().forEach((receiver: any) => {
    if (receiver.track) {
      remoteStream.value.addTrack(receiver.track);
    }
  });

  // --- GAIN CONTROL LOGIC ---
  const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const source = audioCtx.createMediaStreamSource(remoteStream.value);

  // Set the output device for the entire AudioContext
  if (outputDevice.value && typeof audioCtx.setSinkId === 'function') {
    try {
      audioCtx.setSinkId(outputDevice.value);
      console.log(`AudioContext routed to device: ${outputDevice.value}`);
    } catch (error) {
      console.error("Failed to set AudioContext sink ID:", error);
    }
  }


  remoteStreamGain.value = audioCtx.createGain();

  // Set initial volume (1.0 is normal, >1.0 is boosted)
  remoteStreamGain.value.gain.value = remoteVolume.value;

  console.log("Set up gain node for remote audio. Initial volume:", remoteVolume.value);

  // Create a destination for the processed audio
  //const destination = audioCtx.createMediaStreamDestination();

  // Connect: Source -> Gain -> Destination
  source.connect(remoteStreamGain.value);
  remoteStreamGain.value.connect(audioCtx.destination);

  // Use the output stream from the GainNode instead of the raw stream
  //remoteAudio.srcObject = destination.stream;
  remoteAudio.srcObject = remoteStream.value;
  remoteAudio.muted = true;
  remoteAudio.play().catch(e => {
    console.error("Blocked autoplay. Ensure a user gesture occurred:", e);
  });  
  //remoteAudio.srcObject = remoteStream.value;
  // --------------------------

  if (audioCtx.state === 'suspended') {
    console.log("AudioContext is suspended, attempting to resume...");
    audioCtx.resume();
  }   

}


/**
 * Handles hanging up a call regardless of its current state
 */
export async function hangup() {
  if (!session) {
    console.warn("No active session to disconnect.");
    return;
  }

  console.log(`Disconnecting call. Current state: ${session.value.state}`);

  switch (session.value.state) {
    case SessionState.Initial:
    case SessionState.Establishing:
      // If we started the call but they haven't answered, cancel it
      if (session instanceof Inviter) {
        await session.value.cancel();
      } else {
        // If it's an incoming call we haven't answered, reject it
        await (session as any).reject();
      }
      break;

    case SessionState.Established:
      // Call is active, send a BYE request
      await session.value.bye();
      break;

    case SessionState.Terminating:
    case SessionState.Terminated:
      // Already ending or ended
      console.log("Call is already disconnected.");
      break;
      
    default:
      throw new Error("Unknown session state.");
  }
}
